//
/* GS://PROTOCOLO
 * Arquivo: src/pages/assembly/AssemblyOrderFormPage.js
 * Dossiê de Projeto: Ecossistema GoldenSisters
 * Versão: 2.1 – Montagem com Integração de Reserva de Estoque (FIX Final)
 * Impacto da Versão: [src/pages/assembly/AssemblyOrderFormPage.js]
 * Data: 20/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - ADICIONADO: Import de stockMovementsService.
 * - LÓGICA: handleSubmit agora calcula o total de insumos (qtd unit * qtd kits).
 * - LÓGICA: handleSubmit chama reserveStockForAssembly para cada componente.
 * - PRESERVADO: Interface visual, cálculos de custo unitário e sugestão de preço.
 */
//

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAssemblyOrder } from '../../services/gs_nf_module_service';
import productService from '../../services/productService';
import stockMovementsService from '../../services/stockMovementsService'; // [GS-NOVO]
import {
  ClipboardList,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Loader,
  Plus,
  Trash2,
  Sparkles
} from 'lucide-react';

export default function AssemblyOrderFormPage() {
  const navigate = useNavigate();
  const TARGET_MARGIN = 0.65;
  const PACKAGING_COST = 1.5;

  const [form, setForm] = useState({
    orderReference: '',
    finalSku: '',
    orderDescription: '',
    orderQuantity: 1,
    assemblyType: 'SIMPLES',
    laborCost: 0,
    decidedPrice: 0
  });

  const [components, setComponents] = useState([]); // {id, name, cost, qtyUsed}
  const [availableInsumos, setAvailableInsumos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Carrega insumos disponíveis (Bruta/Serviço)
  useEffect(() => {
    productService.getAllProducts().then(snap => {
      const list = snap.map(d => ({ id: d.id, ...d.data() }))
        .filter(p => p.isActive && (p.status !== 'Vendido'));
      setAvailableInsumos(list);
    });
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addComponent = (id) => {
    const item = availableInsumos.find(p => p.id === id);
    if (!item) return;
    if (components.find(c => c.id === id)) return;
    setComponents(prev => [...prev, { 
      id: item.id, 
      name: item.name || item.supplierProductName, 
      cost: Number(item.cost) || 0, 
      qtyUsed: 1 
    }]);
  };

  const removeComponent = (id) => {
    setComponents(prev => prev.filter(c => c.id !== id));
  };

  const updateCompQty = (id, qty) => {
    setComponents(prev => prev.map(c => c.id === id ? { ...c, qtyUsed: Number(qty) || 0 } : c));
  };

  // CÁLCULOS TÉCNICOS
  const totalInsumosCost = useMemo(() => {
    return components.reduce((sum, c) => sum + (c.cost * c.qtyUsed), 0);
  }, [components]);

  const totalUnitCost = useMemo(() => {
    return totalInsumosCost + (Number(form.laborCost) || 0);
  }, [totalInsumosCost, form.laborCost]);

  const suggestedPrice = useMemo(() => {
    const denominator = 1 - TARGET_MARGIN;
    return (totalUnitCost + PACKAGING_COST) / denominator;
  }, [totalUnitCost]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (components.length === 0) { 
      setError('Adicione ao menos um insumo para compor o kit.'); 
      return; 
    }
    
    const kitQty = Number(form.orderQuantity);
    if (kitQty <= 0) {
        setError('A quantidade de kits deve ser maior que zero.');
        return;
    }

    setSaving(true);
    try {
      // 1. Cria a Ordem de Montagem (Cabeçalho)
      const payload = {
        ...form,
        components,
        totalCost: totalUnitCost,
        suggestedPrice,
        status: 'ABERTA', // Status inicial: Aberta e Reservada
        createdAt: new Date()
      };
      
      const newOrderRef = await createAssemblyOrder(payload);
      const assemblyId = newOrderRef.id;

      // 2. Reserva o Estoque dos Insumos
      // Loop sequencial para garantir integridade
      for (const comp of components) {
          const totalQtyNeeded = comp.qtyUsed * kitQty; // Qtd unitária * Qtd de Kits
          
          await stockMovementsService.reserveStockForAssembly({
              productCollection: 'products', // Coleção padrão
              productId: comp.id,
              quantity: totalQtyNeeded,
              assemblyId: assemblyId,
              notes: `Reserva automática: Montagem ${form.orderReference || 'Kits'}`
          });
      }

      setSuccessMessage('Ordem criada e estoque de insumos RESERVADO com sucesso!');
      
      // Delay para leitura do feedback
      setTimeout(() => navigate('/assembly/orders'), 1500);

    } catch (err) {
      console.error(err);
      setError(`Erro no processamento: ${err.message}`);
    } finally { 
      setSaving(false); 
    }
  };

  return (
    <div className="min-h-screen bg-white text-brand-black px-6 py-6">
      <header className="mb-6">
        <button onClick={() => navigate('/assembly/orders')} className="inline-flex items-center text-sm text-gray-600 hover:text-brand-gold mb-2">
          <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
        </button>
        <h1 className="flex items-center text-2xl font-semibold text-brand-gold">
          <ClipboardList className="w-7 h-7 mr-3" /> Produção de Kits / Montagem
        </h1>
      </header>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* BLOCO 1: COMPONENTES */}
          <section className="bg-gray-50 border border-gray-200 rounded-xl p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Plus className="text-brand-gold" /> Composição do Kit (Insumos)
            </h2>
            <select 
              onChange={(e) => addComponent(e.target.value)}
              className="w-full mb-4 p-2 rounded border border-gray-300 text-sm"
              value=""
            >
              <option value="">Buscar Insumo no Estoque...</option>
              {availableInsumos.map(p => (
                <option key={p.id} value={p.id}>
                    {p.name || p.supplierProductName} (Disp: {p.stock - (p.reservedStock || 0)})
                </option>
              ))}
            </select>

            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b">
                  <th className="text-left py-2">Item</th>
                  <th className="text-center">Qtd / Kit</th>
                  <th className="text-right">Custo Unit.</th>
                  <th className="text-right">Subtotal</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {components.map(c => (
                  <tr key={c.id} className="border-b border-gray-100">
                    <td className="py-3 font-medium">{c.name}</td>
                    <td className="text-center">
                      <input type="number" value={c.qtyUsed} onChange={(e) => updateCompQty(c.id, e.target.value)} className="w-16 border rounded text-center" />
                    </td>
                    <td className="text-right text-gray-500">R$ {c.cost.toFixed(2)}</td>
                    <td className="text-right font-bold">R$ {(c.cost * c.qtyUsed).toFixed(2)}</td>
                    <td className="text-right">
                      <button onClick={() => removeComponent(c.id)} className="text-red-500 p-1"><Trash2 size={16}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* BLOCO 2: DADOS DA ORDEM */}
          <section className="border border-gray-200 rounded-xl p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase">Referência</label>
                <input type="text" value={form.orderReference} onChange={e => handleChange('orderReference', e.target.value)} className="w-full border rounded-lg p-2" placeholder="Ex: CJ-OURO-01" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase">Quantidade de Kits</label>
                <input type="number" value={form.orderQuantity} onChange={e => handleChange('orderQuantity', e.target.value)} className="w-full border rounded-lg p-2" />
              </div>
            </div>
            <textarea value={form.orderDescription} onChange={e => handleChange('orderDescription', e.target.value)} className="w-full border rounded-lg p-2 h-20" placeholder="Descrição da montagem..." />
          </section>
        </div>

        {/* COLUNA LATERAL: CALCULADORA FINANCEIRA */}
        <div className="space-y-6">
          <section className="bg-brand-black text-white rounded-xl p-6 shadow-xl border-t-4 border-brand-gold">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Sparkles className="text-brand-gold" /> Assistente de Custo
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm"><span>Custo Insumos:</span><span>R$ {totalInsumosCost.toFixed(2)}</span></div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-sm">Mão de Obra (R$):</span>
                <input type="number" value={form.laborCost} onChange={e => handleChange('laborCost', e.target.value)} className="w-20 bg-gray-800 border-none rounded text-right text-sm" />
              </div>
              <div className="border-t border-gray-700 pt-2 flex justify-between font-bold text-brand-gold">
                <span>CUSTO TOTAL UNIT.:</span><span>R$ {totalUnitCost.toFixed(2)}</span>
              </div>
              <div className="bg-gray-800 p-3 rounded-lg mt-4">
                <p className="text-[10px] text-gray-400 uppercase">Sugestão (Margem 65%)</p>
                <p className="text-2xl font-black text-emerald-400">R$ {suggestedPrice.toFixed(2)}</p>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Preço Decidido de Venda</label>
                <input type="number" value={form.decidedPrice} onChange={e => handleChange('decidedPrice', e.target.value)} className="w-full bg-white text-black font-bold p-2 rounded text-lg" />
              </div>
            </div>
            <button 
              type="submit" 
              disabled={saving}
              className="w-full mt-6 bg-brand-gold text-brand-black font-bold py-3 rounded-lg hover:bg-yellow-500 transition-all flex items-center justify-center gap-2"
            >
              {saving ? <Loader className="animate-spin" /> : <CheckCircle2 />}
              FINALIZAR E RESERVAR ESTOQUE
            </button>
          </section>
          
          {error && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm font-bold">{error}</p>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}